import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useNavigate } from 'react-router-dom'

import { F0Box, F0Card, F0Text } from '@factorialco/f0-react'
import { useAiChat } from 'modules/ai/chat/useAiChat'
import { Building as BuildingIcon, File as FileIcon } from '@factorialco/f0-react/icons/app'

import {
  CreateProcurementTaskFormDocument,
  TaskFormsDataCollectionDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useOne, useOneFeatureAvailable } from 'modules/ai/lib/useOne'

type CategoryValue = 'basic' | 'provider'

const OneFooter = ({
  showProviderButton,
  onClickBasic,
  onClickProvider,
  isCreating,
}: {
  showProviderButton: boolean
  onClickBasic: () => void
  onClickProvider: () => void
  isCreating: boolean
}) => {
  const { visualizationMode, setOpen, setVisualizationMode } = useAiChat()
  const isFullscreen = visualizationMode === 'fullscreen'

  const handleClickBasic = useCallback(() => {
    onClickBasic()
    setVisualizationMode('sidepanel')
    setOpen(false)
  }, [setVisualizationMode, setOpen, onClickBasic])

  const handleClickProvider = useCallback(() => {
    onClickProvider()
    setVisualizationMode('sidepanel')
    setOpen(false)
  }, [setVisualizationMode, setOpen, onClickProvider])

  if (!isFullscreen) {
    return null
  }

  return (
    <F0Box display="flex" gap="lg" flexDirection="column" width="full">
      <F0Text variant="label" content={i18n.t('procurement.task_forms.one_footer.or_start_with')} />
      <F0Box display="grid" columns={showProviderButton ? '2' : '1'} gap="xl" width="full">
        <F0Card
          title={i18n.t('procurement.task_forms.one_footer.basic_form')}
          description={i18n.t('procurement.task_forms.one_footer.basic_form_description')}
          avatar={{
            icon: FileIcon,
            type: 'icon',
          }}
          onClick={isCreating ? undefined : handleClickBasic}
        />
        {showProviderButton && (
          <F0Card
            title={i18n.t('procurement.task_forms.one_footer.provider_form')}
            description={i18n.t('procurement.task_forms.one_footer.provider_form_description')}
            avatar={{
              icon: BuildingIcon,
              type: 'icon',
            }}
            onClick={isCreating ? undefined : handleClickProvider}
          />
        )}
      </F0Box>
    </F0Box>
  )
}

export const useTaskFormOne = ({
  onFallback,
  canCreate,
  showProviderButton,
}: {
  onFallback: () => void
  canCreate: boolean
  showProviderButton: boolean
}) => {
  const oneAvailable = useOneFeatureAvailable()
  const { open: isChatOpen, clear: clearChat } = useAiChat()
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [fullscreen, setFullscreen] = useState(false)
  const [autoOpenChat, setChatOpen] = useState(false)
  const shouldClearOnNextOpen = useRef(false)

  const [createProcurementTaskForm, { loading: isCreating }] = useMutation(
    CreateProcurementTaskFormDocument,
    {
      extractorFn: (data) => data.procurementMutations.createProcurementTaskForm,
    }
  )

  useEffect(() => {
    if (!isChatOpen) {
      setChatOpen(false)
    }
  }, [isChatOpen])

  useEffect(() => {
    if (isChatOpen && shouldClearOnNextOpen.current) {
      shouldClearOnNextOpen.current = false
      clearChat()
    }
  }, [isChatOpen, clearChat])

  const createForm = useCallback(
    async (category: CategoryValue) => {
      if (isCreating) return

      const result = await createProcurementTaskForm({
        variables: {
          name: i18n.t('procurement.task_forms.one_footer.default_name'),
          category,
          companyId: company.id,
        },
        refetchQueries: [TaskFormsDataCollectionDocument],
      })

      const handled = handleResult(result, () =>
        app.setFlash({ type: 'wrong', message: i18n.t('common.error') })
      )
      if (!handled.success) return

      const taskFormId = handled.data?.taskForm?.id
      if (!taskFormId) return

      navigate(navigation.settings.procurement.taskForms.id.edit.fullPath({ id: taskFormId }))
    },
    [isCreating, createProcurementTaskForm, company.id, navigation, navigate]
  )

  const openBasicForm = useCallback(() => createForm('basic'), [createForm])
  const openProviderForm = useCallback(() => createForm('provider'), [createForm])

  const oneOptions = useMemo(
    () => ({
      initialMessage: i18n.t('procurement.task_forms.one.chat_initial_message_new'),
      autoOpenChat,
      fullscreen,
      footer:
        canCreate && fullscreen ? (
          <OneFooter
            showProviderButton={showProviderButton}
            onClickBasic={openBasicForm}
            onClickProvider={openProviderForm}
            isCreating={isCreating}
          />
        ) : undefined,
      // Stable primitive key to prevent footer effect from looping when footer JSX gets new refs.
      footerKey: canCreate && fullscreen,
    }),
    [
      autoOpenChat,
      fullscreen,
      canCreate,
      showProviderButton,
      openBasicForm,
      openProviderForm,
      isCreating,
    ]
  )

  useOne<null>(['procurementForms'], null, oneOptions)

  const handleCreateTaskForm = useCallback(() => {
    if (!canCreate) return
    if (oneAvailable) {
      shouldClearOnNextOpen.current = true
      setFullscreen(true)
      setChatOpen(true)
    } else {
      onFallback()
    }
  }, [canCreate, oneAvailable, onFallback])

  return { handleCreateTaskForm }
}
